import { DateOnly } from '@if/core/date'
import {
  ChangeOwnerOperationDto,
  ClosingSettlementOperationDto,
  CustomerPaymentOperationDto,
  EOperationType,
  InvoiceOperationDto,
  LegacyBalanceNormalizationOperationDto,
  LenderPaymentOperationDto,
  LoanClosingOperationDto,
  OperationInformationsDto,
  OperationLateInformationResultDto,
  OperationResultDto,
  CreateLoanOperationDto,
  ProductRefundOperationDto,
  ChargeOffOperationDto,
} from '@src/api/loan/loan-dto'
import { transformOperationFromDto, transformOperationToDto } from '@src/api/loan/loan-transformer'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { EOperationStatus } from '../constants'

export { EOperationType }

export interface ChangeOwnerOperation extends Omit<ChangeOwnerOperationDto, 'effectiveDate'> {
  effectiveDate: DateOnly
}

export interface CustomerPaymentOperation extends Omit<CustomerPaymentOperationDto, 'effectiveDate'> {
  effectiveDate: DateOnly
  paymentNumber: number
}

export interface InvoiceOperation extends Omit<InvoiceOperationDto, 'effectiveDate'> {
  effectiveDate: DateOnly
}

export interface CreateLoanOperation extends Omit<CreateLoanOperationDto, 'effectiveDate'> {
  effectiveDate: DateOnly
}

export interface ProductRefundOperation extends Omit<ProductRefundOperationDto, 'effectiveDate'> {
  effectiveDate: DateOnly
}

export interface LoanClosingOperation extends Omit<LoanClosingOperationDto, 'effectiveDate'> {
  effectiveDate: DateOnly
}

export interface LenderPaymentOperation extends Omit<LenderPaymentOperationDto, 'effectiveDate'> {
  effectiveDate: DateOnly
}

export interface ClosingSettlementOperation extends Omit<ClosingSettlementOperationDto, 'effectiveDate'> {
  effectiveDate: DateOnly
}

export interface ChargeOffOperation extends Omit<ChargeOffOperationDto, 'effectiveDate'> {
  effectiveDate: DateOnly
}

export interface LegacyBalanceNormalizationOperation
  extends Omit<LegacyBalanceNormalizationOperationDto, 'effectiveDate'> {
  effectiveDate: DateOnly
}

export type Operation =
  | CustomerPaymentOperation
  | InvoiceOperation
  | ChangeOwnerOperation
  | CreateLoanOperation
  | ProductRefundOperation
  | LoanClosingOperation
  | LenderPaymentOperation
  | ClosingSettlementOperation
  | ChargeOffOperation
  | LegacyBalanceNormalizationOperation

export class OperationInformation<TOperation extends Operation = Operation> {
  private static readonly EXECUTED_STATUSES = new Set<EOperationStatus>([
    EOperationStatus.Complete,
    EOperationStatus.Failed,
  ])

  public readonly simulatedOperationId: string
  public readonly operation: TOperation
  public readonly operationResultDto: OperationResultDto
  public readonly lateInformationResult: OperationLateInformationResultDto

  private constructor(
    simulatedOperationId: string,
    operation: TOperation,
    operationResultDto: OperationResultDto,
    lateInformationResult: OperationLateInformationResultDto,
    paymentNumber?: number,
  ) {
    this.simulatedOperationId = simulatedOperationId
    this.operation = operation
    this.operationResultDto = operationResultDto
    this.lateInformationResult = lateInformationResult
    if (paymentNumber) {
      ;(this.operation as CustomerPaymentOperation).paymentNumber = paymentNumber
    }
  }

  public isExecuted(): boolean {
    return OperationInformation.EXECUTED_STATUSES.has(this.operation.operationStatusId)
  }

  public isLocked(canBypassAcpLock = false): boolean {
    return (
      this.isExecuted() ||
      (this.isCustomerPaymentOperation() &&
        this.operation.effectiveDate <
          SafePaymentActionDateService.getInstance().getFirstSafeDate(this.operation.paymentMethodId, canBypassAcpLock))
    )
  }

  public isCustomerPaymentOperation(): this is OperationInformation<CustomerPaymentOperation> {
    return this.operation._internalType === EOperationType.CustomerPaymentOperation
  }

  public modifiedByUser(): boolean {
    return this.isCustomerPaymentOperation() && this.operation.isManualPayment === true
  }

  public static fromDto(
    operationInformationDto: OperationInformationsDto,
    paymentNumber?: number,
  ): OperationInformation {
    const {
      simulatedOperationId,
      operation,
      operationResultDto,
      lateInformationResult: lateInformationResult,
    } = operationInformationDto
    return new OperationInformation(
      simulatedOperationId,
      transformOperationFromDto(operation),
      operationResultDto,
      lateInformationResult,
      paymentNumber,
    )
  }

  public static toDto(operationInformation: OperationInformation): OperationInformationsDto {
    return {
      simulatedOperationId: operationInformation.simulatedOperationId,
      operation: transformOperationToDto(operationInformation.operation),
      operationResultDto: operationInformation.operationResultDto,
      lateInformationResult: operationInformation.lateInformationResult,
    }
  }
}
